/**
 * Der Abschnitt „Zahlung anstoßen" des Zahlungs-Reiters - Portierung von `sec('pay-action', …)` aus
 * `drawerPayTab` (`index.html:8626-8633`) samt `initiatePayment` (`index.html:9880-9894`).
 *
 * Original, woertlich:
 *
 *     sec('pay-action', 'Zahlung anstoßen', `
 *       <div class="payment-action">
 *         <div class="payment-action-copy">
 *           <strong>${o.paymentInitiatedAt ? 'Zahlung wurde angestoßen' : 'Zahlung zur Bearbeitung geben'}</strong>
 *           <span class="${o.paymentInitiatedAt ? 'payment-started' : ''}">${o.paymentInitiatedAt
 *             ? `${esc(fmtDT(o.paymentInitiatedAt))} · ${esc(o.paymentInitiatedBy || 'Unbekannt')}`
 *             : 'Der Klick wird mit Zeitpunkt und Nutzer protokolliert.'}</span>
 *         </div>
 *         <button class="btn-primary" type="button" data-permission="request_payment"
 *           onclick="initiatePayment()">${icon('send')}${o.paymentInitiatedAt ? 'Erneut anstoßen' : 'Zahlung anstoßen'}</button>
 *       </div>`)
 *
 * ================= `data-permission="request_payment"` - die EINZIGE Stelle im Bestand =========
 *
 * Nachgezaehlt: `data-permission` kommt im gesamten `index.html` genau EINMAL vor (`:8632`), an
 * diesem Knopf. Das Attribut steht hier deshalb woertlich - und ist, so wie der Bestand die Rechte
 * je Reiter bildet, in der Richtung WIRKUNGSLOS, fuer die es gedacht war:
 *
 *   DRAWER_TAB_EDIT_PERMISSIONS.pay = ['manage_payments', 'request_payment']   (index.html:8366)
 *   hasPermission(p) = [p].flat().some(item => CURRENT_PERMISSIONS.has(item))  (index.html:5302)
 *
 * `canEditTab` ist damit fuer ein Konto mit `request_payment` schon `true`, und der Durchgang in
 * `openDrawer` (`index.html:9296-9304`) sperrt danach gar nichts mehr. Der im Bestand daneben
 * stehende Kommentar („So bleibt ‚Zahlung anstoßen' für ein Standardkonto bedienbar, während der
 * Rest des Zahlungs-Tabs für dieselbe Rolle nur lesbar ist") beschreibt eine Verengung, die nicht
 * eintritt. Wirksam ist das Attribut nur in der ANDEREN Richtung: einem Konto mit ausschliesslich
 * `manage_payments` nimmt es genau diesen Knopf. Beides ist in `zahlungTab.test.tsx` festgenagelt
 * (Gruppe „BEFUND: data-permission ist wirkungslos") - gemeldet, nicht repariert.
 *
 * ================= Die zweite Rechtepruefung: im Klickhandler ==================================
 *
 * `initiatePayment` prueft `hasPermission('request_payment')` NOCH EINMAL selbst
 * (`index.html:9883`) - unabhaengig davon, ob der Knopf gesperrt ist. Diese Pruefung ist hier
 * uebernommen (`onAnstossen` wird ohne das Recht nicht gerufen); sie ist der einzige Grund, aus dem
 * dieser Reiter - anders als die Reiter „Bestellung", „Tasks" und „Dokumente" - seinen Rechtesatz
 * ueberhaupt liest.
 */
import { hatRecht } from "../drawerRechte.ts";
import { fmtDT } from "../../../lib/format.ts";
import { meldeFehler } from "../../../state/fehlerStapel.ts";
import { Abschnitt } from "../Abschnitt.tsx";
import type { Order } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `index.html:9883`, woertlich (Spec R5). */
const KEIN_RECHT_ANSTOSSEN = "Du darfst keine Zahlung anstoßen";

export interface FreigabeProps {
  bestellung: Order;
  rechte: ReadonlySet<string>;
  /** `initiatePayment()` (`index.html:9880`) - ohne `request_payment` NICHT gerufen (siehe oben). */
  onAnstossen: () => void;
  onZuruecksetzen: () => void;
}

export function Freigabe({ bestellung: o, rechte, onAnstossen, onZuruecksetzen }: FreigabeProps) {
  const angestossen = !!o.paymentInitiatedAt;

  return (
    <Abschnitt schluessel="pay-action" titel="Zahlung anstoßen">
      <div className="payment-action">
        <div className="payment-action-copy">
          <strong>{angestossen ? "Zahlung wurde angestoßen" : "Zahlung zur Bearbeitung geben"}</strong>
          <span className={angestossen ? "payment-started" : ""}>
            {angestossen
              ? `${fmtDT(o.paymentInitiatedAt as string)} · ${(o.paymentInitiatedBy as string) || "Unbekannt"}`
              : "Der Klick wird mit Zeitpunkt und Nutzer protokolliert."}
          </span>
        </div>
        <button
          className="btn-primary"
          type="button"
          data-permission="request_payment"
          onClick={() => {
            // `index.html:9883`, woertlich - der `toast('Du darfst keine Zahlung anstoßen')` des
            // Bestands. Bis Fehleranzeige Paket 1 (Task 8) hatte er in der SPA kein Gegenstueck: der Klick
            // tat einfach nichts (Inventar E). Jetzt am Stapel, im Wortlaut des Bestands (Spec R5).
            if (!hatRecht(rechte, "request_payment")) {
              meldeFehler(KEIN_RECHT_ANSTOSSEN);
              return;
            }
            onAnstossen();
          }}
        >
          <Ikone name="send" />
          {angestossen ? "Erneut anstoßen" : "Zahlung anstoßen"}
        </button>
        {angestossen && (
          <button
            className="btn-ghost"
            type="button"
            data-permission="request_payment"
            onClick={() => {
              if (!hatRecht(rechte, "request_payment")) {
                meldeFehler(KEIN_RECHT_ANSTOSSEN);
                return;
              }
              if (!window.confirm("Zahlung anstoßen zurücknehmen?")) return;
              onZuruecksetzen();
            }}
          >
            Anstoß zurücknehmen
          </button>
        )}
      </div>
    </Abschnitt>
  );
}
